import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StyleTemplate, PhotoData } from '../types/template';
import { useAppContext } from '../context/AppContext';
import Header from './Header';
import AIProcessingLoader from './AIProcessingLoader';
import PhotoUploadArea from './PhotoUploadArea';
import { backendService } from '../services/backendService';
import { aiService } from '../services/aiService';
import { createImageService, RasterConfig, ImageService } from '../services/imageService';
import { UserPlaceholderIcon, ArrowIcon, QuestionIcon, MagicIcon, RefreshIcon, PrintPictureIcon, SpinnerIcon, CloseIcon, DisconnectedIcon } from './Icons';
import './TemplateDetail.css';
import QRCodeGenerator from './QRCodeGenerator';
import MemberRestriction from './MemberRestriction';

interface TemplateDetailProps {
  template: StyleTemplate;
  onBack: () => void;
  userPhoto?: string; // 可选的用户照片URL
}

const TemplateDetail: React.FC<TemplateDetailProps> = ({ template, onBack, userPhoto: initialUserPhoto }) => {
  const navigate = useNavigate();
  const { photoProcessingMode, config } = useAppContext();
  const [userPhoto, setUserPhoto] = React.useState<string | null>(initialUserPhoto || null);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = React.useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting');
  const autoReturnTimerRef = React.useRef<number | null>(null);
  
  // 从 config 中读取 isPrintMode，默认为 false
  const isPrintMode = config?.canPrint === true;
  
  // AI处理相关状态
  const [isAIProcessing, setIsAIProcessing] = React.useState(false);
  const [aiStage, setAiStage] = React.useState<'uploading' | 'converting' | 'completing'>('uploading');
  const [aiProgress, setAiProgress] = React.useState(0);
  const [processedImageUrl, setProcessedImageUrl] = React.useState<string | null>(null);
  
  // 后端上传相关状态
  const [isUploadingToBackend, setIsUploadingToBackend] = React.useState(false);
  const [backendImageUrl, setBackendImageUrl] = React.useState<string | null>(null);
  
  // 光栅合成相关状态
  const [isRasterProcessing, setIsRasterProcessing] = React.useState(false);
  const [rasterResult, setRasterResult] = React.useState<string | null>(null);
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [printed, setPrinted] = React.useState(false);

  // 会员限制相关状态
  const [showMemberRestriction, setShowMemberRestriction] = React.useState(false);
  const [verifiedMemberPhone, setVerifiedMemberPhone] = React.useState<string | null>(null);

  // 创建配置化的ImageService实例
  const imageService = React.useMemo(() => {
    return createImageService(config);
  }, [config]);

  // 当配置更新时，更新ImageService的水印设置
  React.useEffect(() => {
    if (config && imageService) {
      imageService.updateWatermarkSettings(config);
      // console.log('ImageService水印设置已更新:', config);
    }
  }, [config, imageService]);

  // 当 initialUserPhoto 变化时更新 userPhoto 状态
  React.useEffect(() => {
    if (initialUserPhoto) {
      setUserPhoto(initialUserPhoto);
    }
  }, [initialUserPhoto]);

  // 记录照片处理方式选择
  React.useEffect(() => {
    if (photoProcessingMode) {
      // 这里可以根据选择的处理方式进行不同的初始化或配置
      if (photoProcessingMode === 'raster') {
        console.log('将启用光栅合成功能，用户照片显示为正方形');
      } else if (photoProcessingMode === 'style-only') {
        console.log('将只进行风格化处理，用户照片在黑色背景中按比例缩小显示');
      }
    }
  }, [photoProcessingMode]);

  // 初始化SSE连接
  React.useEffect(() => {
    let mounted = true;

    const initializeSSE = async () => {
      try {
        setConnectionStatus('connecting');
        
        // 连接SSE
        const newSessionId = await backendService.connect(template.id);
        
        if (mounted) {
          setSessionId(newSessionId);
          setConnectionStatus('connected');
          
          // 监听照片上传
          backendService.onPhotoReceived((photoData: PhotoData) => {
            if (mounted) {
              // 构建完整的base64图片URL
              const imageUrl = photoData.imageData.startsWith('data:') 
                ? photoData.imageData 
                : `data:${photoData.mimetype};base64,${photoData.imageData}`;
              
              setUserPhoto(imageUrl);
            }
          });
          
          // 监听连接状态变化
          backendService.onConnectionChange((status) => {
            if (mounted) {
              setConnectionStatus(status);
            }
          });
        }
      } catch (error) {
        console.error('初始化SSE失败:', error);
        if (mounted) {
          setConnectionStatus('error');
        }
      }
    };

    initializeSSE();

    return () => {
      mounted = false;
      backendService.disconnect();
    };
  }, [template.id]);
  
  // 自动返回首页的倒计时
  React.useEffect(() => {
    let secondsLeft = 300;
    if (autoReturnTimerRef.current) {
      clearInterval(autoReturnTimerRef.current);
    }
    
    // 每秒更新倒计时
    const timer = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) {
        clearInterval(timer);
        setUserPhoto(null);
        setProcessedImageUrl(null);
        setSessionId(null);
        setConnectionStatus('disconnected');
        // 自动返回首页
        onBack();
      }
    }, 1000);
    
    // 保存timer的引用到ref中
    autoReturnTimerRef.current = timer as unknown as number;

    // 组件卸载时清除定时器
    return () => {
      clearInterval(timer);
    };
  }, [onBack]);
  
  // 二维码的URL
  const appUrl = process.env.REACT_APP_URL || window.location.origin;
  let qrCodeValue = `${appUrl}/upload/${template.id}?session=${sessionId || ''}`;
  
  // 如果是仅风格化模式，添加aspect参数
  if (photoProcessingMode === 'style-only') {
    qrCodeValue += '&aspect=0.75';
  }

  // 处理现场拍照按钮点击
  const handleTakePhoto = () => {
    const cameraUrl = `/camera/${template.id}`;
    navigate(cameraUrl);
  };

  // 获取连接状态显示
  const getConnectionStatusDisplay = () => {
    switch (connectionStatus) {
      case 'disconnected':
        return { text: '连接断开' };
      case 'error':
        return { text: '连接错误' };
      default:
        return { text: '未知状态' };
    }
  };

  // 保留这个函数以防其他地方使用，但在界面上不再显示文本
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statusDisplay = getConnectionStatusDisplay();

  // 重置倒计时函数
  const resetCountdownTimer = useCallback((seconds: number) => {
    // 清除当前的定时器
    if (autoReturnTimerRef.current) {
      clearInterval(autoReturnTimerRef.current);
    }
    
    // 重新启动300秒倒计时
    let secondsLeft = seconds || 300;
    const timer = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) {
        clearInterval(timer);
        setUserPhoto(null);
        setProcessedImageUrl(null);
        setSessionId(null);
        setConnectionStatus('disconnected');
        onBack();
      }
    }, 1000);
    
    autoReturnTimerRef.current = timer as unknown as number;
  }, [onBack]);

  // 将图片上传到后端服务器的函数
  const uploadImageToBackend = useCallback(async (imageUrl: string): Promise<string> => {
    try {
      setIsUploadingToBackend(true);
      
      // 将图片URL转换为Blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // 生成文件名
      const timestamp = Date.now();
      const filename = `styled_photo_${timestamp}.jpg`;
      
      // 上传到后端
      const uploadResult = await backendService.uploadToBackend(blob, filename);
      
      if (uploadResult && uploadResult.url) {
        setBackendImageUrl(uploadResult.url);
        return uploadResult.url;
      } else {
        throw new Error('上传失败：未返回图片URL');
      }
    } catch (error) {
      console.error('上传到后端失败:', error);
      throw error;
    } finally {
      setIsUploadingToBackend(false);
    }
  }, []);

  // 光栅合成函数
  const handleRasterComposite = useCallback(async (): Promise<string> => {
    if (!userPhoto || !processedImageUrl) {
      throw new Error('需要用户照片和处理后的图片');
    }

    setIsRasterProcessing(true);
    
    try {
      // 使用标准光栅配置
      const config: RasterConfig = ImageService.getPresetConfigs().S120;
      
      const result = await imageService.rasterComposite(
        { url: userPhoto },
        { url: processedImageUrl },
        config
      );
      setRasterResult(result);
      
      return result;
    } catch (error) {
      console.error('光栅合成失败:', error);
      throw new Error('光栅合成失败，请重试');
    } finally {
      setIsRasterProcessing(false);
    }
  }, [userPhoto, processedImageUrl, imageService]);

  // 执行打印的核心逻辑
  const executePrint = useCallback(async (memberPhone?: string | null) => {
    if (!userPhoto || !processedImageUrl) {
      console.error('需要用户照片和处理后的图片');
      return;
    }

    try {
      // 重置倒计时
      resetCountdownTimer(300);
      setIsPrinting(true);
      
      let printImageUrl = rasterResult;
      
      // 根据处理模式决定是否需要光栅合成
      if (photoProcessingMode === 'style-only') {
        // 仅风格化模式：直接使用处理后的图片，不进行光栅合成
        printImageUrl = processedImageUrl;
        console.log('仅风格化模式：跳过光栅合成，直接使用风格化图片');
      } else {
        // 光栅合成模式：如果还没有光栅结果，先进行合成
        if (!printImageUrl) {
          printImageUrl = await handleRasterComposite();
        }
      }
      
      // 根据处理模式设置打印配置
      const printConfig = photoProcessingMode === 'style-only' ? {
        // The physical paper for style-only printing is 153mm (width) x 100mm (height).
        // Use landscape orientation so the photo fills the full paper horizontally.
        paperSize: 'Custom' as const,
        customSize: { width: 153, height: 100 }, // 153mm x 100mm 照片纸 (width x height)
        orientation: 'landscape' as const,
        quality: 'high' as const,
        photoAspectRatio: '153:100', // 填满整张纸的比例 (width:height)
        margins: { top: 0, right: 0, bottom: 0, left: 0 } // 无边距
      } : {
        paperSize: 'Letter' as const,
        orientation: 'landscape' as const,
        quality: 'high' as const,
        photoAspectRatio: '1:1', // 1:1 比例用于光栅合成模式
        margins: { top: 0, right: 0, bottom: 0, left: 0 }
      };
      
      // 执行静默打印
      await imageService.silentPrintWithConfig(printImageUrl, printConfig);
      setPrinted(true);
      
      // 使用传入的 memberPhone 参数或状态中的 verifiedMemberPhone
      const phoneToLog = memberPhone || verifiedMemberPhone;
      
      // 打印成功后，如果有会员验证信息，记录到后端
      if (phoneToLog) {
        backendService.paintingMemrberLog(phoneToLog, template.name).catch((error) => {
          console.error('记录会员打印日志失败:', error);
        });
      }
    } catch (error) {
      console.error('打印失败:', error);
      alert(`打印失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [userPhoto, processedImageUrl, rasterResult, handleRasterComposite, resetCountdownTimer, photoProcessingMode, imageService, verifiedMemberPhone, template.name]);

  // 打印照片处理函数
  const handlePrintPhoto = useCallback(async () => {
    // 检查是否需要会员验证
    if (config?.onlyVIP === true) {
      // 显示会员限制对话框
      setShowMemberRestriction(true);
    } else {
      // 直接执行打印
      await executePrint();
    }
  }, [config, executePrint]);

  // 会员验证通过后的回调
  const handleMemberVerified = useCallback(async (phone: string) => {
    setVerifiedMemberPhone(phone);
    setShowMemberRestriction(false);
    await executePrint(phone);
  }, [executePrint]);

  // 重置所有状态参数
  const resetAllStates = useCallback(() => {
    setUserPhoto(null);
    setProcessedImageUrl(null);
    setBackendImageUrl(null);
    setRasterResult(null);
    setIsAIProcessing(false);
    setAiStage('uploading');
    setAiProgress(0);
    setIsUploadingToBackend(false);
    setIsRasterProcessing(false);
    setIsPrinting(false);
    setPrinted(false);
    setShowMemberRestriction(false);
    setVerifiedMemberPhone(null);
  }, []);

  // AI图片处理函数
  const handleStartCreation = async () => {
    if (!userPhoto) {
      console.error('没有用户照片');
      return;
    }

    try {
      // 重置倒计时
      resetCountdownTimer(300);

      // 清除之前的处理结果（用于重新制作）
      setProcessedImageUrl(null);
      setBackendImageUrl(null);
      setRasterResult(null);
      setPrinted(false);

      setIsAIProcessing(true);
      setAiProgress(0);

      // 第一步：上传照片到AI服务器
      setAiStage('uploading');
      setAiProgress(10);

      const uploadResult = await aiService.uploadImage(userPhoto);

      if (!uploadResult.success) {
        throw new Error(uploadResult.message || '图片上传失败');
      }

      setAiProgress(30);

      // 第二步：AI风格化转换
      setAiStage('converting');
      setAiProgress(40);

      setTimeout(() => {
        setAiProgress(60); // 模拟转换开始
      }, 3000);

      const convertResult = await aiService.convertPhoto({
        imageId: uploadResult.data.imageId,
        templateId: template.id
      });

      if (!convertResult.success) {
        throw new Error(convertResult.message || 'AI转换失败');
      }

      setAiProgress(70);

      // 处理AI转换结果
      const handleCompleted = async (convertedImageUrl: string) => {
        setProcessedImageUrl(convertedImageUrl);
        setAiProgress(90);

        // 上传风格化照片到后端服务器
        try {
          // 当convertedImageUrl是localhost地址时，上传到后端
          if (convertedImageUrl.startsWith('http://localhost')) {
            const backendUrl = await uploadImageToBackend(convertedImageUrl);
            setBackendImageUrl(backendUrl);
          }
        } catch (uploadError) {
          console.error('上传到后端失败:', uploadError);
          // 上传失败不影响主流程，只记录错误
        }

        setAiProgress(100);

        // 第三步：完成处理
        setAiStage('completing');
        // 短暂延迟以显示完成状态
        setTimeout(() => {
          setIsAIProcessing(false);
          setAiProgress(0);
        }, 2000);
      };

      if (convertResult.data.status === 'processing') {
        let attempts = 0;
        const maxAttempts = 30;
        const errorHandler = (error: any) => {
          console.error('轮询过程中出错:', error);
          alert(`处理失败: ${error instanceof Error ? error.message : '未知错误'}`);
          setIsAIProcessing(false);
          setAiProgress(0);
        };
        const pollStatus = async (): Promise<void> => {
          attempts++;
          setAiProgress(70 + (attempts / maxAttempts) * 20);
          if (attempts >= maxAttempts) {
            throw new Error('AI处理超时');
          }
          const statusResult = await aiService.pollConvertStatus(convertResult.data.taskId);
          console.log("轮询状态:", statusResult.data.status);
          if (statusResult.data.status === 'completed') {
            await handleCompleted(statusResult.data.convertedImageUrl);
            return;
          } else if (statusResult.data.status === 'failed') {
            throw new Error('AI转换失败');
          } else {
            setTimeout(() => pollStatus().catch(errorHandler), 3000);
          }
        };
        pollStatus().catch(errorHandler);
      } else if (convertResult.data.status === 'completed') {
        await handleCompleted(convertResult.data.convertedImageUrl);
      }
    } catch (error) {
      console.error('AI处理失败:', error);
      alert(`处理失败: ${error instanceof Error ? error.message : '未知错误'}`);
      setIsAIProcessing(false);
      setAiProgress(0);
    }
  };

  return (
    <div 
      className="template-detail"
      style={{
        backgroundImage: `url(/bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="template-detail-overlay">
        <Header 
          onBack={onBack}
          title={`制作同款 > ${template.name}`}
        />
        
        {/* SSE连接状态指示器 - 只在连接失败时显示 */}
        {(connectionStatus === 'error' || connectionStatus === 'disconnected') && (
          <div className={`sse-status error`}>
            <DisconnectedIcon width="24" height="24" style={{ color: '#ff1744' }} />
          </div>
        )}
        
        <div className={`template-detail-content ${processedImageUrl ? 'processed' : ''}`}>
          <div className={`template-preview-group-container ${userPhoto ? 'has-photo' : ''}`}>
            <div className={`template-preview-section ${userPhoto ? 'has-photo' : ''} ${processedImageUrl ? 'processed-hidden' : ''}`}>
              <div className="template-preview">
                <div className={`user-photo-container ${userPhoto ? 'has-photo' : ''} ${photoProcessingMode === 'style-only' ? 'style-only-mode' : ''}`}>
                  {userPhoto ? (
                    <img 
                      src={userPhoto}
                      alt="用户上传的照片"
                      className={`user-photo ${photoProcessingMode === 'style-only' ? 'style-only-photo' : ''}`}
                    />
                  ) : (
                    <div className="photo-placeholder">
                      <div className="question-mark">
                        <UserPlaceholderIcon width="24" height="24" />
                      </div>
                      <p className="photo-placeholder-text">请扫码上传或拍摄照片</p>
                    </div>
                  )}
                </div>
                
                {/* Transform arrow */}
                <div className="transform-arrow">
                  <ArrowIcon width="48" height="48" />
                </div>

                <div className="template-preview-container">
                  <img
                    src={template.imageUrl}
                    alt="模板预览图"
                    className="template-preview-image"
                  />
                  {/* 当有用户照片但还未处理时显示半透明问号图层 */}
                  {userPhoto && !processedImageUrl && (
                    <div className="template-preview-overlay">
                      <div className="preview-question-mark">
                        <QuestionIcon width="120" height="120" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 上传区域 - 只在没有用户照片时显示 */}
            {!userPhoto && (
              <PhotoUploadArea
                qrCodeValue={qrCodeValue}
                sessionId={sessionId}
                onTakePhoto={handleTakePhoto}
              />
            )}
          </div>

          {/* 打印预览区域 - 当AI处理完成后显示 */}
          {processedImageUrl && userPhoto && (
            <div className="template-preview-group-container" style={{ width: '100%' }}>
              <div className="print-preview-section">
                <div className="print-preview-container">
                  <div className="layered-image-preview" 
                    style={{ aspectRatio: photoProcessingMode === 'style-only' ? '3 / 4' : '1 / 1' }}
                  >
                    {photoProcessingMode === 'style-only' ? (
                      // 仅风格化模式：只显示风格化后的图片
                      <img 
                        src={processedImageUrl}
                        alt="风格化图片"
                        className="layer-image style-only-result"
                      />
                    ) : (
                      // 光栅合成模式：显示分层效果或合成结果
                      <>
                        {!rasterResult && (<>
                          <img 
                            src={userPhoto}
                            alt="用户照片"
                            className="layer-image user-layer"
                          />
                          <img 
                            src={processedImageUrl}
                            alt="风格化图片"
                            className="layer-image style-layer"
                          />
                        </>)}

                        {/* 光栅合成结果图层 */}
                        {rasterResult && (
                          <img 
                            src={rasterResult}
                            alt="光栅合成结果"
                            className="layer-image raster-layer"
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>

                {userPhoto && (
                  <div className="result-actions-section">
                    <div className='primary-actions'>
                      <button 
                        className="button button-lg button-primary" 
                        onClick={() => {
                          resetAllStates();
                          navigate('/');
                        }}
                      >
                        <CloseIcon width="40" height="40" />
                        关闭
                      </button>
                    </div>

                    <div className='secondary-actions'>
                      {processedImageUrl && (<button
                        className="button-link"
                        onClick={handleStartCreation}
                      >
                        <MagicIcon width="22" height="22" />
                        重新制作
                      </button>)}

                      <button
                        className="button-link"
                        onClick={() => {
                          setUserPhoto(null);
                          setProcessedImageUrl(null); // 清除处理结果
                          setBackendImageUrl(null); // 清除后端URL
                          // console.log('清除用户照片和处理结果');
                        }}
                      >
                        <RefreshIcon width="20" height="20" />
                        更换照片
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="upload-section download-section">
                <div className="qr-code-container">
                  <div className="qr-code-wrapper">
                    <QRCodeGenerator 
                      value={backendImageUrl || processedImageUrl}
                      size={280}
                      responsive={true}
                      className="qr-code"
                    />
                  </div>
                </div>
                <div className="upload-instruction">
                  <h4>扫码下载风格化照片</h4>
                  <p>使用手机扫描下方二维码，下载您的风格化照片</p>
                  {isUploadingToBackend && (
                    <p className="upload-status">正在生成下载链接...</p>
                  )}
                </div>

                <div className='print-button-container'>
                  {processedImageUrl && !printed && isPrintMode && (<button 
                    className="button button-lg button-primary" 
                    onClick={handlePrintPhoto}
                    disabled={isAIProcessing || isPrinting || isRasterProcessing}
                  >
                    {isPrinting || isRasterProcessing ? <SpinnerIcon width="40" height="40" /> : <PrintPictureIcon width="40" height="40" />}
                    {isPrinting ? '正在打印' : (isRasterProcessing && photoProcessingMode !== 'style-only') ? '光栅合成中' : '打印照片'}
                  </button>)}
                </div>
              </div>
            </div>
          )}

          {/* 开始制作按钮 - 当有用户照片并且未处理时显示 */}
          {userPhoto && !processedImageUrl && (
            <div className="start-creation-section">
              <div className='primary-actions'>
                {!processedImageUrl && (<button 
                  className="button button-lg button-primary" 
                  onClick={handleStartCreation}
                  disabled={isAIProcessing}
                >
                  <MagicIcon width="36" height="36" />
                  开始制作
                </button>)}
              </div>

              <div className='secondary-actions'>
                <button
                  className="button-link"
                  onClick={() => {
                    setUserPhoto(null);
                    setProcessedImageUrl(null); // 清除处理结果
                    setBackendImageUrl(null); // 清除后端URL
                    // console.log('清除用户照片和处理结果');
                  }}
                >
                  <RefreshIcon width="20" height="20" />
                  更换照片
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* AI处理加载器 */}
      {isAIProcessing && (
        <AIProcessingLoader 
          stage={aiStage}
          progress={aiProgress}
        />
      )}

      {/* 会员限制对话框 */}
      {showMemberRestriction && (
        <MemberRestriction
          onVerified={handleMemberVerified}
          onClose={() => setShowMemberRestriction(false)}
        />
      )}
    </div>
  );
};

export default TemplateDetail;
